import { useState } from "react";
import { toast } from "sonner";
import { Bell } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { SETTINGS } from "@/constants/testIds/app";

export default function Settings() {
  const { user, updateSettings, logout } = useAuth();
  const [busy, setBusy] = useState(false);

  const toggleReminders = async () => {
    setBusy(true);
    try {
      await updateSettings({ reminders_enabled: !user.reminders_enabled });
      toast.success(user.reminders_enabled ? "Reminders turned off" : "Gentle reminders on");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={SETTINGS.container} className="max-w-2xl">
      <div className="label-caps mb-4">Settings</div>
      <h1 className="font-serif-display text-4xl sm:text-5xl text-[#2C2520] tracking-tight">
        A few small preferences.
      </h1>

      <div className="mt-10 space-y-4">
        <section className="bg-[#F5F2EA] border border-[#E8E3D5] rounded-[12px] p-6">
          <div className="label-caps mb-2">Account</div>
          <p className="text-[#2C2520]">{user?.name}</p>
          <p className="text-sm text-[#2C2520]/60">{user?.email}</p>
        </section>

        <section className="bg-[#F5F2EA] border border-[#E8E3D5] rounded-[12px] p-6 flex items-start gap-4">
          <div className="w-10 h-10 rounded-[10px] bg-[#FDFBF7] border border-[#E8E3D5] flex items-center justify-center flex-shrink-0">
            <Bell className="w-5 h-5 text-[#4A5D4E]" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <h3 className="font-serif-display text-lg text-[#2C2520]">Gentle daily check-in</h3>
            <p className="text-sm text-[#2C2520]/70 mt-1">
              A soft nudge to pause and notice how you feel. In-app only.
            </p>
          </div>
          <button
            data-testid={SETTINGS.remindersToggle}
            onClick={toggleReminders}
            disabled={busy}
            className={
              "relative w-11 h-6 rounded-full transition-colors " +
              (user?.reminders_enabled ? "bg-[#4A5D4E]" : "bg-[#E8E3D5]")
            }
          >
            <span
              className={
                "absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform " +
                (user?.reminders_enabled ? "translate-x-5" : "translate-x-0.5")
              }
            />
          </button>
        </section>

        <button
          onClick={logout}
          className="px-5 py-2.5 rounded-[12px] border border-[#E8E3D5] text-sm text-[#2C2520] hover:bg-[#F5F2EA] transition-colors"
        >
          Sign out
        </button>

        <section className="pt-4 border-t border-[#E8E3D5]">
          <div className="label-caps mb-3">Legal</div>
          <div className="flex flex-wrap gap-3 text-sm">
            <Link to="/privacy" className="text-[#4A5D4E] underline underline-offset-4">Privacy policy</Link>
            <Link to="/disclosure" className="text-[#4A5D4E] underline underline-offset-4">Affiliate disclosure</Link>
          </div>
        </section>
      </div>
    </div>
  );
}
